import { useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import request from "@/shared/api/request";
import ManyToOneSelect from "@/shared/forms/ManyToOneSelect";
import ManyToManySelect from "@/shared/forms/ManyToManySelect";
import { checkUserRights, type RightDefinition, type UserRightDraft } from "@/shared/forms/userRightsValidation";

type RoleTemplate = { id: number; name: string; rights: RightDefinition[] };

export default function RoleAssignmentAssistant({ allRights, onComplete }: {
  allRights: Record<string, RightDefinition>;
  onComplete: (roleId: number, draft: UserRightDraft[]) => void;
}) {
  const roles = useQuery<RoleTemplate[]>({ queryKey: ["userRoleTemplates"], queryFn: () => request({ url: "/data/userRoleTemplates", method: "GET" }) });
  const [selectedId, setSelectedId] = useState(0);
  const [activeRole, setActiveRole] = useState<RoleTemplate | null>(null);
  const [draft, setDraft] = useState<UserRightDraft[]>([]);
  const [step, setStep] = useState(-1);
  const selected = roles.data?.find((role) => role.id === selectedId);
  const questions = activeRole?.rights.filter((right) => right.type !== "Boolean") ?? [];
  const current = questions[step];
  const entry = draft.find((item) => item.right === current?.name);
  const issues = activeRole ? [
    ...activeRole.rights.filter((right) => !(right.name in allRights)).map((right) => `Berechtigung nicht zur Zuweisung verfügbar: ${right.description}`),
    ...activeRole.rights.filter((right) => !["Boolean", "ManyToOne", "ManyToMany"].includes(right.type)).map((right) => `Fehlerhafter Eingabetyp: ${right.description}`),
    ...checkUserRights(draft, allRights, false),
  ] : [];
  const changeSelection = (value: string) => setDraft((previous) => previous.map((item) => item.right === current.name ? { ...item, stringVal: value } : item));
  const hasAnswer = !current || !!entry?.stringVal;
  const close = () => { setActiveRole(null); setDraft([]); };

  return <Box sx={{ mt: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <TextField select fullWidth label="Rollenpaket" value={selectedId} onChange={(event) => setSelectedId(Number(event.target.value))} disabled={roles.isLoading || roles.isError}>
        <MenuItem value={0}>{roles.data?.length ? "Rollenpaket auswählen" : "Keine Rollenpakete vorhanden"}</MenuItem>
        {(roles.data ?? []).map((role) => <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>)}
      </TextField>
      <Button disabled={!selected || roles.isFetching} onClick={() => {
        setActiveRole(selected!); setStep(-1);
        setDraft(selected!.rights.map((right) => ({ id: 0, right: right.name, intVal: right.type === "Boolean" ? 1 : null, stringVal: null })));
      }}>Rolle zuweisen</Button>
    </Box>
    {roles.isError ? <Alert severity="error">Rollenpakete konnten nicht geladen werden.</Alert> : null}
    <Dialog open={!!activeRole} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{activeRole?.name}</DialogTitle>
      <DialogContent>
        {step === -1 ? <Alert severity="warning">Die bisherigen Rechte werden vollständig ersetzt. Die Änderung wird erst mit „Speichern“ in der Benutzerverwaltung gespeichert. Abbrechen lässt die bisherigen Rechte unverändert.</Alert> : current ? <>
          <Typography sx={{ mb: 2 }}>Frage {step + 1} von {questions.length}: {current.question?.trim() || current.description}</Typography>
          {current.type === "ManyToOne" ? <ManyToOneSelect entity={current.entity} currentId={Number(entry?.stringVal ?? 0)} setId={(id) => changeSelection(id > 0 ? String(id) : "")} label={current.question?.trim() || current.description} /> : current.type === "ManyToMany" ? <ManyToManySelect entity={current.entity} currentIds={entry?.stringVal ? entry.stringVal.split(",").map(Number) : []} setIds={(ids) => changeSelection(ids.join(","))} label={current.question?.trim() || current.description} /> : <Alert severity="error">Die Konfiguration dieser Berechtigung muss korrigiert werden.</Alert>}
        </> : <>
          <Typography>Dieses Rechtepaket wird in den Entwurf übernommen:</Typography>
          {(activeRole?.rights ?? []).map((right) => <Typography key={right.name}>{right.description}</Typography>)}
          {issues.length ? <Alert severity="error" sx={{ mt: 1 }}>{issues.map((issue, index) => <div key={index}>{issue}</div>)}</Alert> : null}
        </>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Abbrechen</Button>
        {step >= 0 ? <Button onClick={() => setStep(step - 1)}>Zurück</Button> : null}
        {step < questions.length ? <Button disabled={!activeRole?.rights.length || (step >= 0 && !hasAnswer)} onClick={() => setStep(step + 1)}>Weiter</Button> : <Button disabled={issues.length > 0} onClick={() => { onComplete(activeRole!.id, draft); close(); }}>Rechtepaket übernehmen</Button>}
      </DialogActions>
    </Dialog>
  </Box>;
}
